import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    BrainCircuit,
    MessagesSquare,
    Rocket,
    Settings,
    BarChart3,
    Users2,
    Wand2,
    Gauge,
    DollarSign,
    TrendingUp,
    ScanLine,
    FileText
} from "lucide-react";

/* ------------------------------------------------------------------
 * XerooConversationLanding – v3 (2025‑06‑09)
 * Cambios solicitados por el usuario.
 * ------------------------------------------------------------------ */

const BRAND = "#5860D5";

export default function XerooConversationLanding() {
    /* ---------------- Scroll-driven parallax ---------------- */
    const { scrollY } = useScroll();
    const y1 = useTransform(scrollY, [0, 600], [0, 140]);
    const y2 = useTransform(scrollY, [0, 600], [0, 300]);

    /* ---------------- Conversation ---------------- */
    const conversation = [
        {
            q: "¿Qué hacen en Xeroo?",
            a: "Creamos soluciones de Inteligencia Artificial que automatizan tareas, optimizan procesos y generan valor medible en semanas."
        },
        {
            q: "¿Cómo pueden ayudarme?",
            a: "Detectamos oportunidades de alto impacto en tus operaciones, diseñamos un plan y te acompañamos para demostrar ROI positivo."
        },
        {
            q: "¿Cuánto tarda un proyecto típico?",
            a: "Un primer entregable funcional suele estar listo en 2‑4 semanas; luego iteramos y escalamos según resultados."
        },
        {
            q: "¿Necesito grandes volúmenes de datos?",
            a: "¡Tranquilo! No hace falta ser un gigante tecnológico. Podemos combinar tus datos con modelos pre‑entrenados y técnicas de aprendizaje transferido para empezar incluso con datos pequeños."
        },
        {
            q: "¿Cómo empiezo?",
            a: "Simple: agendá una reunión de descubrimiento y recibí un roadmap de IA personalizado para tu negocio."
        }
    ];

    /* ---------------- Benefit cards (9) ---------------- */
    const benefits = [
        { icon: <BrainCircuit className="h-6 w-6 text-white" />, text: "Atención al cliente 24/7 con chatbots IA" },
        { icon: <BarChart3 className="h-6 w-6 text-white" />, text: "Pronósticos de demanda confiable para optimizar inventarios" },
        { icon: <Users2 className="h-6 w-6 text-white" />, text: "Segmentación inteligente de clientes mediante análisis de comportamiento" },
        { icon: <Wand2 className="h-6 w-6 text-white" />, text: "Generación automática de contenido" },
        { icon: <Gauge className="h-6 w-6 text-white" />, text: "Optimización de rutas logísticas y entregas" },
        { icon: <DollarSign className="h-6 w-6 text-white" />, text: "Precios dinámicos que maximizan márgenes" },
        { icon: <TrendingUp className="h-6 w-6 text-white" />, text: "Análisis de sentimiento en redes sociales" },
        { icon: <ScanLine className="h-6 w-6 text-white" />, text: "Detección de oportunidades de Upselling y Crosselling" },
        { icon: <BrainCircuit className="h-6 w-6 text-white" />, text: "Personalización de campañas de marketing automáticamente" },
        { icon: <BarChart3 className="h-6 w-6 text-white" />, text: "Reportes financieros inteligentes al instante" },
        { icon: <FileText className="h-6 w-6 text-white" />, text: "Clasificación y respuesta de solicitudes automáticamente" },
        { icon: <Users2 className="h-6 w-6 text-white" />, text: "Soporte interno a la operación con chatbots internos" }
    ];

    /* ---------------- Success cases ---------------- */
    const cases = [
        { sector: "Retail", metric: "‑18% costos logísticos", time: "6 semanas" },
        { sector: "eCommerce", metric: "+35% conversión", time: "4 semanas" },
        { sector: "Industrias", metric: "‑20% paradas de línea", time: "8 semanas" }
    ];

    /* ---------------- Components ---------------- */
    const ChatBubble = ({ children, me, i }) => (
        <motion.div
            initial={{ opacity: 0, x: me ? 60 : -60, y: 20 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: i * 0.25, duration: 0.6, ease: "easeOut" }}
            className={`flex ${me ? "justify-end" : "justify-start"}`}
        >
            <div
                className={`max-w-md rounded-2xl p-4 md:p-5 text-base leading-snug shadow-xl backdrop-blur-sm ${me ? `bg-[#5860D5] text-white rounded-br-none` : "bg-white/70 rounded-bl-none"
                    }`}
            >
                {children}
            </div>
        </motion.div>
    );

    /* ---------------- Render ---------------- */
    return (
        <div className="min-h-screen w-full bg-gradient-to-b from-[#fafafe] via-[#f4f6ff] to-white">
            {/* Navbar */}
            <header className="sticky top-0 z-30 backdrop-blur-lg bg-white/60 ring-1 ring-white/20 shadow-[0_0_20px_rgba(88,96,213,0.25)]">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-2">
                    {/* Logo sin sombra */}
                    <img src="/xeroo-logo-violet.png" alt="Xeroo" className="h-9" />
                    <nav className="hidden md:flex items-center gap-6 font-medium text-sm tracking-wide">
                        <a href="#conversation" className="hover:text-[#5860D5]">Preguntas</a>
                        <a href="#benefits" className="hover:text-[#5860D5]">Beneficios</a>
                        <a href="#cases" className="hover:text-[#5860D5]">Casos</a>
                        <a href="#cta" className="bg-[#5860D5] text-white rounded-full px-4 py-1.5 shadow-lg hover:scale-105 transition-transform">Agendar reunión</a>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section id="start" className="relative overflow-hidden pt-24 md:pt-32 flex flex-col items-center text-center px-6 select-none">
                {/* <motion.img src="/heroia.png" alt="grid" style={{ y: y2 }} className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] opacity-5 pointer-events-none" />
                <motion.img src="/heroia.png" alt="decor" style={{ y: y1 }} className="absolute top-16 left-1/4 w-40 opacity-40 pointer-events-none" /> */}

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-4xl md:text-6xl font-extrabold leading-tight bg-gradient-to-r from-[#6a71ff] via-[#5860D5] to-[#a165ff] bg-clip-text text-transparent"
                >
                    Inteligencia Artificial <br />que impulsa tu negocio
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                    className="mt-6 max-w-2xl text-lg md:text-xl text-gray-700"
                >
                    Soluciones de IA para generar crecimiento y eficiencia en <span className="font-semibold">semanas</span>.<br />
                    Automatización y optimización al alcance de tu empresa.
                </motion.p>

                <motion.a
                    href="#cta"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#5860D5] text-white font-semibold shadow-lg hover:scale-105 active:scale-95 transition-transform"
                >
                    <Rocket className="h-5 w-5" /> Agendar reunión
                </motion.a>
            </section>

            {/* Conversation */}
            <section id="conversation" className="mt-28 flex flex-col items-center px-4">
                <div className="max-w-3xl w-full space-y-6">
                    {conversation.map(({ q, a }, idx) => (
                        <React.Fragment key={idx}>
                            <ChatBubble me={false} i={idx * 2}>{q}</ChatBubble>
                            <ChatBubble me={true} i={idx * 2 + 1}>{a}</ChatBubble>
                        </React.Fragment>
                    ))}
                </div>
            </section>

            {/* Benefits */}
            <section id="benefits" className="mt-32 bg-white py-20">
                <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-3 bg-gradient-to-r from-[#6a71ff] via-[#5860D5] to-[#a165ff] bg-clip-text text-transparent">Beneficios concretos</h2>
                <p className="text-center text-lg text-gray-600 mb-12">Resultados que podés medir desde la primer entrega</p>
                <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefits.map((b, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ delay: 0.1 * i, duration: 0.5 }}
                            className="flex items-start gap-4 bg-[#5860D5] p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 text-white"
                        >
                            <div className="p-3 rounded-full bg-white/15">
                                {b.icon}
                            </div>
                            <p className="leading-snug flex-1">{b.text}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Success Cases */}
            <section id="cases" className="bg-gradient-to-r from-[#eff2ff] to-white py-20">
                <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-3">Casos de éxito</h2>
                <p className="text-center text-lg text-gray-600 mb-10">Empresas que aceleraron su retorno con Inteligencia Artificial</p>
                <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 px-6">
                    {cases.map((c, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ delay: 0.15 * i, duration: 0.5 }}
                            className="bg-white p-8 rounded-2xl shadow-xl text-center flex flex-col"
                        >
                            <span className="text-[#5860D5] font-semibold tracking-wide uppercase">{c.sector}</span>
                            <span className="mt-4 text-2xl font-bold">{c.metric}</span>
                            <span className="mt-2 text-sm text-gray-500">en {c.time}</span>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section id="cta" className="relative py-28 overflow-hidden">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-4xl mx-auto bg-[#5860D5] text-white rounded-3xl shadow-2xl px-10 py-14 flex flex-col md:flex-row items-center gap-6"
                >
                    <MessagesSquare className="h-16 w-16 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold leading-snug">¿Listo para aprovechar la IA?</h3>
                        <p className="mt-2 text-lg opacity-90">Agendá una reunión estratégica gratuita con nuestros expertos.</p>
                    </div>
                    <a
                        href="https://calendly.com/xeroo/descubrimiento"
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#5860D5] font-semibold shadow-lg hover:scale-105 transition-transform whitespace-nowrap"
                    >
                        <Rocket className="h-5 w-5" /> Agendar reunión
                    </a>
                </motion.div>
            </section>

            {/* Footer */}
            <footer className="text-center text-sm py-10 text-gray-500">© {new Date().getFullYear()} Xeroo. Todos los derechos reservados.</footer>
        </div>
    );
}
